import React, { useState, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { BasicTable } from '../../components/basic-table';
import { Icon } from '@cotera/client/app/components';

const STORAGE_KEY = 'atc-restate-query-history';
const MAX_HISTORY = 20;

interface QueryHistoryItem {
  query: string;
  timestamp: number;
}

interface QueryHistory {
  queries: QueryHistoryItem[];
}

const loadQueryHistory = (): QueryHistory => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) {
      return { queries: [] };
    }
    return JSON.parse(stored);
  } catch {
    return { queries: [] };
  }
};

const saveQueryToHistory = (query: string): void => {
  const history = loadQueryHistory();
  const newItem: QueryHistoryItem = {
    query,
    timestamp: Date.now(),
  };

  // Remove duplicate if exists
  const filtered = history.queries.filter((item) => item.query !== query);

  // Add new item at the beginning and limit to MAX_HISTORY
  const updated: QueryHistory = {
    queries: [newItem, ...filtered].slice(0, MAX_HISTORY),
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
};

const DEFAULT_QUERY =
  "SELECT * FROM sys_invocation WHERE status = 'completed' LIMIT 10";

export const CustomRestateQuery: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [queryHistory, setQueryHistory] = useState<QueryHistory>(
    loadQueryHistory()
  );
  const [results, setResults] = useState<Record<string, any>[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const client = useUntenantedClient();

  const mutation = useMutation({
    mutationFn: async (queryText: string) => {
      const res = await client.atcVisibility.executeCustomQuery({
        query: queryText,
      });
      return Assert.assertOk(res);
    },
    onSuccess: (data) => {
      setResults(data.rows);
      setError(null);
      saveQueryToHistory(query);
      setQueryHistory(loadQueryHistory());
    },
    onError: (err: any) => {
      setError(err?.message || 'Failed to execute query');
      setResults(null);
    },
  });

  const handleExecute = () => {
    if (query.trim() === '') {
      return;
    }
    mutation.mutate(query);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleExecute();
    }
  };

  const handleHistorySelect = (value: string) => {
    if (value === 'none') {
      return;
    }
    const historyItem = queryHistory.queries.find(
      (item) => item.timestamp.toString() === value
    );
    if (historyItem !== undefined) {
      setQuery(historyItem.query);
    }
  };

  // Generate dynamic columns based on results
  const columns = useMemo<ColumnDef<Record<string, any>>[]>(() => {
    if (results === null || results.length === 0) {
      return [];
    }

    const firstRow = results[0];
    if (firstRow === undefined) {
      return [];
    }

    return Object.keys(firstRow).map((key) => ({
      accessorKey: key,
      header: key,
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value === undefined) {
          return <span className="text-gray-400">null</span>;
        }
        if (typeof value === 'object') {
          return (
            <span className="font-mono text-xs">{JSON.stringify(value)}</span>
          );
        }
        return <span>{String(value)}</span>;
      },
      enableColumnFilter: true,
    }));
  }, [results]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="space-y-2">
      <CollapsibleTrigger className="flex w-full items-center gap-2 justify-between px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors">
        <span className="font-semibold">Custom Restate Query</span>
        <Icon
          icon={isOpen ? 'chevron-up' : 'chevron-down'}
          className="w-4 h-4"
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              SQL Query
            </label>
            {queryHistory.queries.length > 0 && (
              <Select onValueChange={handleHistorySelect} value="none">
                <SelectTrigger className="w-[200px] h-8">
                  <SelectValue placeholder="Load from history..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Load from history...</SelectItem>
                  {queryHistory.queries.map((item) => (
                    <SelectItem
                      key={item.timestamp}
                      value={item.timestamp.toString()}
                    >
                      <span className="truncate max-w-[300px] block">
                        {item.query.substring(0, 50)}
                        {item.query.length > 50 ? '...' : ''}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <Textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="font-mono text-sm"
            rows={6}
          />
          <Button
            onClick={handleExecute}
            disabled={mutation.isPending || query.trim() === ''}
          >
            {mutation.isPending ? 'Executing...' : 'Execute Query'}
          </Button>
        </div>

        {error !== null && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800 font-medium">Error</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {results !== null && results.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">
              Results ({results.length} rows)
            </h3>
            <BasicTable
              data={results}
              columns={columns}
              enableSorting={true}
              enableFiltering={true}
              enablePagination={true}
              pageSize={20}
            />
          </div>
        )}

        {results !== null && results.length === 0 && (
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
            <p className="text-sm text-gray-600">Query returned no results</p>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};
